package uk.co.nickthecoder.kyd.appearance

import uk.co.nickthecoder.glok.backend.PartialTexture
import uk.co.nickthecoder.glok.property.boilerplate.intProperty
import uk.co.nickthecoder.glok.scene.Image
import uk.co.nickthecoder.kyd.*
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * Common features shared by [Pose] and [NinePatch].
 *
 * A partial texture is a rectangular region of a [Graphic].
 */
abstract class PartialGraphic(

    parent: ParentResource,
    name: String,
    graphicPath: String

) : AbstractParentResource(parent, name), PinnedImage, ResolvableResource {

    /**
     * Only valid while the .kyd file is being loaded.
     * Once loaded, [resolveReferences] is called, and afterward, [graphicPath] is reset to blank and no longer used.
     */
    var graphicPath: String = graphicPath
        private set(v) {
            field = v
            resolveReferences()
        }

    /**
     * The graphic which contains the pose.
     * This is lazily evaluated using [graphicPath], so that we can
     * load an entire `.kyd` file without caring if the definition of the [Pose]
     * is found BEFORE the definition of its corresponding [Graphic].
     */
    lateinit var graphic: Graphic
        internal set

    // Properties, so that repeating [Pose]s can listen to when they change, and rebuild the repeating texture.
    internal val graphicLeftProperty by intProperty(0)
    internal val graphicTopProperty by intProperty(0)
    internal val graphicWidthProperty by intProperty(0)
    internal val graphicHeightProperty by intProperty(0)

    /**
     * Together with [graphicTop], [graphicWidth], [graphicHeight], determines the rectangular region within the [graphic].
     * Measured in pixels, with (0,0) being at the top left of the image.
     */
    var graphicLeft: Int by graphicLeftProperty

    /**
     * Together with [graphicLeft], [graphicWidth], [graphicHeight], determines the rectangular region within the [graphic].
     * Measured in pixels, with (0,0) being at the top left of the image.
     * NOTE. Almost all other coordinates have the Y axis pointing upwards, whereas
     * here, the Y axis points downwards.
     */
    var graphicTop: Int by graphicTopProperty

    /**
     * Together with [graphicTop], [graphicLeft] and [graphicHeight], determines the rectangular region within the [graphic].
     */
    var graphicWidth: Int by graphicWidthProperty

    /**
     * Together with [graphicTop], [graphicLeft] and [graphicWidth], determines the rectangular region within the [graphic].
     */
    var graphicHeight: Int by graphicHeightProperty


    /**
     * When we animate an [Actor], we change the actor's position ([Actor.position])
     *
     * [pin] corresponds to the actors position.
     * For a walking character, it is usually nice to make this level with the ground
     * (i.e. near the bottom middle of the pose).
     *
     * For many other character, the referenceOffset may be the center of the pose.
     */
    override var pin: Vector2 = Vector2.ZERO

    override val image: Image
        get() = PartialTexture(
            graphic.texture,
            graphicLeft.toFloat(),
            graphicTop.toFloat(),
            graphicWidth.toFloat(),
            graphicHeight.toFloat()
        )

    override fun resolveReferences(): String? {
        val graphic = find(graphicPath) as? Graphic
        if (graphic == null) {
            return "Pose ${path()} : Failed find graphic : $graphicPath"
        } else {
            this.graphic = graphic
            graphicPath = ""
            return null
        }
    }

}
